/** English — the source of truth for the message set.
 *
 * `MessageKey` is `keyof typeof en`, so every other locale module is typed
 * `Record<MessageKey, string>` and `tsc` rejects a missing or stray key. The
 * design notes on each group live here and here only; a translator reads
 * them to know what a string has to fit and what must stay untranslated.
 *
 * Technical diagnostics (thrown Error messages, the worker, debug info) stay
 * English in every locale; only human-facing UI copy lives here. */

export const en = {
  metaDescription:
    "Free live weather maps in your browser: GFS, ECMWF and AIFS forecasts, radar and satellite loops, typhoon tracks, 3D radar and skew-T soundings.",
  /** The live viewer's document title, what a search result for `/` shows:
   * the field by its localized label, the kind of thing this is, and the
   * model with its horizon (`{hours}H` stays as the instrument reads it).
   * `· Xue` is appended by pagemeta.ts. */
  pageTitleLive: "{variable} forecast map · {model} {hours}H",
  /** The same for a live observation dataset (a rolling window): no
   * forecast, and `{hours}` is how far back the window reaches. */
  pageTitleLiveObservation: "{variable} observation map · {model} · last {hours}H",
  mapAria: "Global temperature and precipitation forecast map",
  modelSwitchAria: "Dataset",
  variableSwitchAria: "Weather variable",
  /** The variable buttons' small label, carried visually hidden beside the
   * code for the accessible name: in Chinese it glosses the English code
   * (TEMP 气温); repeating the code in English would be redundant, so there
   * it carries the instrument detail instead (TEMP 2M), matching the model
   * buttons' code + detail structure. Keep a translation short — it reads
   * aloud right after the code. */
  varTemp: "2M",
  varPrecip: "RATE",
  varWind: "10M",
  varSolar: "FLUX",
  varRadar: "CREF",
  varOrog: "OROG",
  /** The aurora tile: the field is a probability, so the gloss is the
   * unit. */
  varAurora: "PROB",
  /** The river tiles: the discharge's six-hour step, the return period
   * (RP), and the upstream area's unit-less word. */
  varDis06: "6H",
  varDisrp: "RP",
  varUpstream: "AREA",
  /** The satellite tile: the channel's nominal wavelength in µm after the
   * code (SAT IR 10.4). */
  varInfrared: "IR 10.4",
  varShortwave: "IR 3.9",
  // The composite's gloss: RGB is the term in every language.
  varDustRgb: "RGB",
  // The confidence field's gloss: the algorithm's name, an instrument code
  // in every language.
  varDustCf: "DEBRA",
  varZhouye: "ZHOUYE",
  // The true colour picture's gloss: the tile reads TRUE COLOR.
  varTrueColor: "COLOR",
  /** The surface diagnostics: gust and CAPE are surface fields (SFC,
   * the same detail the codes carry), cloud cover is the whole column. */
  varGust: "SFC",
  varCloud: "TOTAL",
  varCape: "SFC",
  /** The new GFS fields' rail glosses: precipitable water is the whole
   * column, the precipitation type and the inhibition are surface fields,
   * the boundary layer height is a depth. */
  varPwat: "COLUMN",
  varPtype: "SFC",
  varCin: "SFC",
  varHpbl: "HEIGHT",
  /** Visibility is a surface field; the dew point and apparent temperature
   * are 2 m fields; the two upper-air families name their quantity. */
  varVis: "SFC",
  varDewPoint: "2M",
  varFeelsLike: "2M",
  varOmega: "VERT",
  varThetaE: "EQUIV",
  /** Clear-air turbulence names its quantity, EDR, in every Latin-script
   * locale (the instrument word, like CAT beside it); CJK glosses the code. */
  varCat: "EDR",
  // The experiment's derived layers' captions: one word under the code.
  varInflow: "INFLOW",
  varFront: "FRONT",
  varHotspot: "IR 3.9",
  varFfwi: "FIRE WX",
  varFwalert: "RED FLAG",
  /** The ocean set's tiles: the skin temperature is read as the SST it is
   * over water but the gloss says what the field is, sea ice heads with
   * its cover, the waves with their significant height. */
  varSst: "SKIN",
  varIce: "COVER",
  varWave: "HEIGHT",
  /** The aerosol families' tiles: the optical depth heads with the whole
   * column (its species are the sheet's chips), the particulate matter
   * with the 2.5 µm cut — a size, so the same in every language. */
  varAod: "TOTAL",
  varPm: "2.5",
  /** The surface dust concentration's own tile: a surface field (SFC);
   * CJK glosses name the quantity. */
  varDustsfc: "SFC",
  /** The soil families' tiles: the top layer heads each, and the level
   * row picks the others by index (a layer is not a depth: the models
   * cut the soil differently). CJK glosses name the quantity. */
  varTsoil: "LAYER 1",
  varSoilw: "LAYER 1",
  /** The upper-air families' tiles: relative humidity and the water vapour
   * flux have no surface member, so the gloss names the field rather than a
   * level. */
  varHumidity: "REL",
  varVapourFlux: "FLUX",
  // The pressure family's buttons carry the level in the code ("MSLP",
  // "500MB"); the gloss says what the field is, once for all eight heights.
  varPressure: "SEA LVL",
  varHeight: "HEIGHT",
  /** The rail tile standing for the whole pressure family; the surface
   * itself is picked on the timeline capsule's level row. */
  varPressureField: "FIELD",
  /** The gloss on a generic rail tile: a bundle the run publishes that this
   * build has no tile of its own for. Before it is opened the id is all
   * there is to say about it. */
  varUnknownLayer: "LAYER",

  /** The layer rail shows one character per layer — a single-glyph name in
   * the CJK locales, or the Latin symbol the field is written with (Z for
   * radar reflectivity) everywhere else. These are WMO symbols, not words:
   * a locale whose script has no conventional single glyph keeps them. The
   * code and its gloss ride along, visually hidden, so each tile still names
   * itself. */
  glyphTmp2m: "T",
  glyphPrate: "R",
  glyphWind10m: "W",
  glyphDswrf: "S",
  glyphCref: "Z",
  // I for the infrared imagery.
  glyphIr104: "I",
  glyphIr039: "S",
  // D for the dust composite.
  glyphDustrgb: "D",
  // C for the dust confidence.
  glyphDustcf: "C",
  // Z for the ZHOUYE confidence, after its name.
  glyphZhouye: "Z",
  // T for the true colour picture.
  glyphTruecolor: "T",
  // The aurora probability: its initial.
  glyphAurora: "A",
  // N is the synoptic-code letter for total cloud amount; the gust and CAPE
  // take their initials.
  glyphGust: "G",
  glyphTcdc: "N",
  glyphCape: "C",
  // ω and θ are the symbols the two upper-air quantities are written with
  // in every script; the surface fields take their initials.
  glyphVis: "V",
  // The new GFS fields take their initials: precipitable water, inhibition,
  // the boundary layer and the precipitation type.
  glyphPwat: "P",
  glyphCin: "I",
  glyphHpbl: "B",
  glyphPtype: "Y",
  glyphDpt2m: "D",
  glyphAptmp2m: "A",
  glyphVvel: "ω",
  glyphThetae: "θ",
  // ε is the eddy dissipation rate's own symbol in every script.
  glyphCat: "ε",
  // The experiment's two derived layers (`?x=`): M for moist, F for front.
  glyphInflow: "M",
  glyphFront: "F",
  glyphHotspot: "H",
  glyphFfwi: "W",
  glyphFwalert: "R",
  // S for the sea surface, I for ice, H for Hs — the significant wave
  // height's own symbol (W is the wind's).
  glyphTmpsfc: "S",
  glyphIce: "I",
  glyphWave: "H",
  // τ is the optical depth's own symbol in every script; µ, of the
  // microgram per cubic metre a particulate concentration is read in.
  glyphAod: "τ",
  glyphPm: "µ",
  // D for dust: the glyph of the tile, not a WMO letter.
  glyphDustsfc: "D",
  // E is the station model's letter for the state of the ground; M the
  // initial of moisture.
  glyphTsoil: "E",
  glyphSoilw: "M",
  glyphPressure: "P",
  // U is the WMO symbol for relative humidity; the vapour flux takes the F
  // of flux.
  glyphRh: "U",
  glyphQflux: "F",
  /** The rail tiles' hover tooltip: the full name of the layer, in plain
   * words, since an icon and a WMO letter do not explain themselves. A
   * family tile names the quantity (the level row picks the surface); a
   * single layer reuses its data-card label (`varLabel*`). */
  tipTemp: "Temperature",
  tipHumidity: "Relative humidity",
  tipWind: "Wind",
  tipPressure: "Pressure and geopotential height",
  tipVapourFlux: "Water vapour flux",
  tipCloud: "Cloud cover",
  tipOmega: "Vertical velocity",
  tipThetaE: "Equivalent potential temperature",
  tipCat: "Clear-air turbulence",
  /** Sea ice and the waves are family tiles; the skin temperature reuses
   * its data-card label. */
  tipIce: "Sea ice",
  tipWave: "Waves",
  /** The aerosol families' tiles: the optical depth and the particulate
   * matter, each a family whose species and size cuts are chips. */
  tipAod: "Aerosol optical depth",
  tipPm: "Particulate matter",
  tipTsoil: "Soil temperature",
  tipSoilw: "Soil moisture",
  /** The satellite tile: a family tile, the infrared window and the
   * Dust RGB composite behind it. */
  tipSatellite: "Satellite imagery",
  levelSwitchAria: "Level",
  levelCaption: "Level",
  /** The level row's caption for the lines' group when the fill has a level
   * row of its own beside it. */
  linesCaption: "Lines",
  /** The level row's caption for the precipitation's own group: the rate,
   * then the windows it is summed over (`?accum=`). */
  accumCaption: "Total",
  /** The rate button's gloss in that group. */
  accumRateGloss: "rate",
  /** A window button's gloss; `{window}` is its instrument code ("24H"). */
  accumTotalGloss: "{window} total",
  modelHeading: "Datasets",
  /** The pressed lines member over a filled field is the overlay's off
   * switch; the tooltip says so, since the × alone is a hint. */
  linesRemoveTitle: "Press again to remove the lines",
  /** The layer rail's three sections, each a group with a name: the
   * fields (one on screen at a time), the overlays drawn over it, the
   * marks of the point products. */
  fieldSectionAria: "Field",
  overlaySectionAria: "Overlays",
  markSectionAria: "Marks",
  /** The rail's MORE tile, which opens the field sheet: its tooltip, its
   * gloss (visually hidden after the code MORE) and its glyph, a plus in
   * every script. The sheet's name and heading, in sentence case like the model
   * sheet's; the group headings inside it, in the same style. */
  tipMore: "All layers of this run",
  varMore: "Layers",
  glyphMore: "+",
  fieldSheetAria: "Layers",
  fieldSheetHeading: "Layers",
  fieldGroupTemperature: "Temperature",
  fieldGroupMoisture: "Moisture",
  fieldGroupSoil: "Soil",
  fieldGroupWind: "Wind",
  fieldGroupDynamics: "Dynamics",
  fieldGroupRadiation: "Radiation & visibility",
  fieldGroupAerosol: "Aerosols & air quality",
  fieldGroupOcean: "Ocean",
  fieldGroupHydrology: "Rivers & floods",
  fieldGroupSatellite: "Satellite",
  fieldGroupSpace: "Space weather",
  fieldGroupFire: "Fire",
  modelGroupForecast: "Forecast models",
  modelGroupRadar: "Radar",
  fieldGroupOther: "Other",
  fieldGroupImagery: "Daily imagery: NASA GIBS",
  gibsTrueColor: "TRUE",
  gibsFalseColor: "FIRE",
  gibsTipTrue: "True colour",
  gibsTipFire: "Fire false colour: burn scars dark red",
  /** The particle switch as a rail tile in the overlay section: the gloss
   * after the code PARTICLES, and its glyph (the tooltip and accessible
   * name are `particlesToggle` / `particlesToggleAria` below). */
  varParticles: "FLOW",
  glyphParticles: "≈",
  /** The volume switch in the overlay section, which draws a cloud volume
   * over the field: its gloss after the code CLW3D and its glyph (q, the
   * mixing ratio; its tooltip is `varLabelCloud3d`). */
  varVolume: "3D CLOUD",
  glyphVolume: "Q",
  /** The level row's ALONE member, a switch after the lines' surfaces:
   * its gloss (the visually hidden word after the instrument code ALONE,
   * which stays English) and its tooltip. */
  levelAlone: "Lines only",
  levelAloneTitle: "Lines alone: the field goes; press again to put it back",
  /** The credits: a round mark at the foot of the right-hand column opening
   * a sheet with one row per source, with the map's own credit line beside
   * it where the screen has room. The trigger's string is the mark's
   * accessible name (the mark itself is a glyph); the row names are proper
   * nouns and stay as they are; the glosses are translated, in sentence case,
   * with a colon after the kind of data and commas or semicolons after that
   * (never a middle dot). */
  creditsTrigger: "Sources",
  creditsHeading: "Data & credits",
  creditsGfs: "Forecast data: GFS 0.25° and sflux",
  creditsHrrr: "Forecast data: HRRR 3 km, contiguous US",
  creditsGefsAero: "Aerosol forecast: GEFS-Aerosols 0.25°, AOD and surface PM",
  /** The CMA's sand-and-dust model, the WMO SDS-WAS Asian Center's,
   * carried by WMO WIS2; the model and programme names stay as written. */
  creditsCmaDust: "Dust forecast: CMA-CW v1.0/DUST (WMO SDS-WAS Asian Center) 0.15° over Asia, via WMO WIS2",
  /** FMI's global air-quality model; the model name stays as written. */
  creditsSilam: "Air-quality forecast: FMI SILAM v6.1 global 0.2°, CC BY 4.0",
  /** The seasonal run: one forecast nine months deep, so the strip under
   * the track reads in months rather than in days. */
  creditsCfs: "Seasonal forecast: CFSv2, 9 months, 6-hourly",
  creditsEcmwf: "Contains modified ECMWF data, CC BY 4.0",
  /** The IFS HRES line carries the attribution ECMWF's licence asks for on
   * an adapted work, and names Open-Meteo, which forwards the centre's
   * real-time archive and is where the resampling to 0.1° happens. */
  creditsIfsHres: "Adapted from ECMWF IFS by ECMWF, licensed under CC BY 4.0; via Open-Meteo; IFS HRES ~9 km, resampled to 0.1°",
  creditsMrms: "Radar observations: MRMS, contiguous US",
  creditsNexrad: "Single-site radar: base reflectivity and velocity, US",
  /** The JMA line carries the attribution its terms of use ask for (a
   * source named and the processing noted); the sheet row's name is the
   * agency's website, as the terms spell it. */
  creditsJma: "Radar precipitation: JMA nowcast, Japan (regridded)",
  /** The Himawari line names the agency, the spacecraft and the host, and
   * notes the processing, as NOAA's terms ask. */
  creditsHimawari: "Satellite imagery: Himawari-9 AHI, JMA, on NOAA Open Data (reprojected)",
  /** The GOES line names the programme, the two spacecraft by slot and
   * the host; NOAA's open data asks for the source to be named. */
  creditsGoes: "Satellite imagery: GOES-19 East & GOES-18 West ABI, NOAA NESDIS, on NOAA Open Data (reprojected)",
  /** The Meteosat line carries the attribution EUMETSAT's data policy
   * asks for on redistributed data ("Contains modified EUMETSAT Meteosat
   * data [year]"), the licence, and the hourly cadence that licence
   * allows. `{year}` is the year of distribution, the current one, which
   * `applyStaticMessages` stamps at render time so the copy never goes
   * stale. */
  creditsMeteosat: "Satellite imagery: contains modified EUMETSAT Meteosat data {year}; Meteosat-12 FCI, hourly cycle, CC-BY-4.0 (reprojected)",
  /** The aurora line names the model and the agency; NOAA's US
   * government work needs no licence but asks for the source. */
  creditsAurora: "Space weather: aurora probability, NOAA SWPC OVATION model",
  creditsEfas: "River discharge: Copernicus EMS EFAS v5, Europe 1′; cases only",
  /** ERA5 is CC-BY-4.0 on the Climate Data Store, which asks for the
   * service to be named; the map line carries the "contains modified"
   * credit itself. */
  creditsEra5: "Reanalysis: Copernicus C3S ERA5 (ECMWF), global 0.25°, hourly, CC-BY-4.0; cases only",
  /** CAMS is CC-BY-4.0 on the Atmosphere Data Store, credited like ERA5. */
  creditsCams: "Aerosol analysis: Copernicus CAMS (ECMWF), global 0.4°, 3-hourly, CC-BY-4.0; cases only",
  creditsSounding: "Radiosonde soundings: GTS TEMP via WIS2, national met services",
  creditsAirport: "Airport METAR & TAF: world met services via NOAA/NWS AWC",
  creditsFirms: "Active fires: LANCE FIRMS NRT, VIIRS 375 m and MODIS, NASA ESDIS",
  creditsGibs: "Daily imagery: MODIS and VIIRS corrected reflectance, NASA ESDIS",
  creditsRadar: "Radar mosaic",
  creditsBasemap: "Basemap: © OpenStreetMap contributors",
  /** The relief under the fields. Mapterhorn asks for the project to be
   * named; the elevation models behind each region are far too many to list
   * here, so the row links to Mapterhorn's own attribution page, which does. */
  creditsTerrain: "Terrain relief: © Mapterhorn, elevation model sources",
  creditsPeaks: "Mountain peak names & heights: CC0",
  creditsCode: "Source code: ringsaturn/xue",
  caseTag: "Case",
  caseSummaryAria: "Case summary",
  runCycle: "Model run",
  // A record of days (a case whose timeline is `daily`) has no cycle:
  // the line that names one names the days the record spans.
  recordSpan: "Record span",
  // An observation dataset has no run cycle and no lead time: the line that
  // names a forecast's cycle names the window's newest observation instead,
  // and a frame is an observation, not a forecast.
  latestObservation: "Latest observation",
  // A nowcast's frames are valid ahead of the clock (the aurora grid is valid
  // when the solar wind it was computed from reaches the Earth), so its newest
  // frame is a forecast a little way off, not an observation.
  latestNowcast: "Latest nowcast",
  awaitingData: "Awaiting data",
  validTimeLabel: "Valid time",
  observationTimeLabel: "Observation time",
  statsAria: "Stats for nerds",
  statsCloseAria: "Close stats",
  bufferedFrames: "Buffered",
  downloadedBytes: "Downloaded",
  formatLabel: "Format",
  // The data card's readout for a session on the Zarr channel — the
  // default wherever a run ships a store; "Xue" is the container a run
  // published before, or `?backend=xue`. A format name is instrument text
  // — "Xue", "Zarr" — so it stays "Zarr" in every locale.
  formatZarr: "Zarr",
  preloadProgressAria: "Forecast data preload progress",
  statDataset: "Dataset",
  statGrid: "Grid",
  statFrameCache: "Frame cache",
  statDecodeTime: "Decode time",
  statDecodeRate: "Decode rate",
  statViewport: "Viewport sampling",
  statConnection: "Connection",
  awaitingManifest: "Awaiting manifest",
  legendPrateAria: "Precipitation rate color scale",
  timelineAria: "Forecast time controls",
  playAnimation: "Play animation",
  pauseAnimation: "Pause animation",
  /** The speed button reads "12 FPS" in every locale (an instrument value,
   * like the forecast hour); only its accessible name is translated. */
  playbackSpeed: "Playback speed",
  /** The corner button that opens the export sheet (a long press saves
   * again with the last settings); while a file is being made it opens the
   * sheet again, where the export can be cancelled. The sheet's format, size and rate options
   * ("GIF", "1080P", "12 FPS") stay as the instrument reads them; its
   * captions are short sentence-case labels like the other sheet headings.
   * `exportSummary` says what the file will come to: `{size}` reads
   * `1920×1080`, `{bytes}` `14 MB`. */
  exportOpenAria: "Export the loop",
  exportSheetAria: "Export the loop",
  exportHeading: "Export",
  exportFormat: "Format",
  exportSize: "Size",
  exportFps: "Rate",
  exportRange: "Frames",
  exportFrom: "From",
  exportTo: "To",
  exportRun: "Export",
  exportCancel: "Cancel",
  exportSummary: "{size}, {frames} frames, {seconds} s, about {bytes}",
  exportCapturing: "Capturing {done}/{total}",
  exportEncoding: "Encoding {percent}%",
  exportFailed: "Export failed",
  readingManifest: "Loading manifest",
  forecastHourAria: "Forecast hour",
  forecastDaysAria: "Daily forecast segments",
  observationDaysAria: "Daily segments",
  mapMenuAria: "Map options",
  menuStats: "Stats for nerds",
  menuCopyDebug: "Copy debug info",
  errorTitle: "Data unavailable",
  retry: "Retry",
  /** The language picker: a round icon button in the top-right column and a
   * panel of endonyms under it. The endonyms themselves are never
   * translated — each language names itself, in its own script — so the only
   * copy here is the word "Language" on the trigger and the panel heading.
   * The heading is a sheet heading, in sentence case like the model
   * sheet's. */
  langPickerAria: "Language",
  langPickerHeading: "Language",
  themeToggleAria: "Toggle light / dark",
  /** The view tile under the zoom buttons (viewcontrol.ts): the globe, the
   * 3D relief and its cast shadows as switches, then back to north and back to a flat camera —
   * the last two shown only while the map is turned or tilted — and the
   * ruler and the area tool (measure.ts), whose names say how to end a
   * shape. Tooltips and accessible names. */
  viewGlobeAria: "Globe",
  viewTerrainAria: "3D terrain",
  viewContoursAria: "Contour lines",
  viewResetNorthAria: "Reset to north",
  viewResetPitchAria: "Reset tilt",
  viewBoxAria: "Storm close-up: drag a box over it",
  viewSectionAria: "Vertical section: drag a line",
  viewDistanceAria: "Measure distance: click points, click the last again to finish",
  viewAreaAria: "Measure area: click corners, click the first or last again to finish",
  /** The wind layer's own control, in the transport capsule: the colored
   * speed field is always drawn, and this turns the particle animation over
   * it on and off. Icon-only, like the three circles above, so the name is
   * carried by the accessible label and a visually hidden word. */
  particlesToggle: "Particles",
  particlesToggleAria: "Toggle wind particle animation",

  dataInterrupted: "Data loading interrupted",
  errorHint: "{message}. Make sure make mvp has been run and the page is served via make serve.",
  loadFailed: "Load failed",
  /** The map's WebGL context is lost again on every restore (a wedged GPU
   * process); only restarting the browser brings it back. */
  webglLost: "WebGL unavailable: quit the browser fully and reopen it",
  saveData: "saver",
  debugCopied: "Debug info copied",
  copyFailed: "Copy failed",
  bundleResident: "Bundle fully buffered",
  // A narrowed view fetches only the tiles it shows, so it never reaches
  // whole-bundle residency — and saying so beats a progress bar that stalls.
  viewportResident: "Viewport fully buffered",
  streamingOnDemand: "Streaming on demand",
  receivingBundle: "Receiving forecast bundle",
  receivingBundlePercent: "Receiving bundle {percent}%",
  readingIndex: "Reading data index",
  initializingDecoder: "Initializing decoder",
  framesReady: "{count} frames ready",
  readingData: "Loading data",
  bundleLoadFailed: "Forecast bundle failed to load",
  workerStartFailed: "Decode worker failed to start",
  decodeFailed: "Decode failed",
  frameDecodeFailed: "Frame decode failed: {message}",
  legendAria: "{label} color scale",
  // The experimental composite's key (`?x=true`): one row per derived
  // layer beside its swatch, under the fill's bar. Short — a noun phrase
  // that fits on one line at 10px mono next to a 10px square.
  legendInflow: "Warm moist inflow",
  legendFront: "Frontal zone (θe gradient)",
  legendHotspot: "Fire hotspot (3.9 µm contextual test)",
  legendFfwi: "Fosberg fire weather index",
  legendFwalert: "RH under 30 % with gusts of 10 m/s or more",
  /** The Dust RGB's key: what each colour of the composite is a sign
   * of, one row per swatch in place of the bar (the GOES-R Quick Guide's
   * reading). Short noun phrases, one line at 10px mono. */
  legendDustDust: "Dust",
  legendDustThickHigh: "Thick high cloud",
  legendDustThinHigh: "Thin high cloud",
  legendDustMid: "Mid-level cloud",
  legendDustLow: "Low warm cloud",
  legendDustDesert: "Hot desert surface",
  legendDustSurface: "Sea and vegetated land",
  legendTruecolorCloud: "Cloud and snow",
  legendTruecolorSea: "Sea",
  legendTruecolorLand: "Land and vegetation",
  legendTruecolorNightCloud: "Cloud at night (infrared)",
  legendTruecolorNightGround: "Clear sky at night",
  /** The precipitation type's key: one row per WMO code table 4.201 class
   * GFS reports, in place of the bar (the field is categorical). Short noun
   * phrases, one line at 10px mono; "no precipitation" has no swatch. */
  legendPtypeRain: "Rain",
  legendPtypeFreezingRain: "Freezing rain",
  legendPtypeSnow: "Snow",
  legendPtypeIcePellets: "Ice pellets",
  /** The EFAS return-period classes, each the flood level a river cell's
   * discharge has reached; the years stay numerals. */
  legendDisrpBelow: "Below 1.5-year level",
  legendDisrp1p5: "≥ 1.5-year level",
  legendDisrp2: "≥ 2-year level",
  legendDisrp5: "≥ 5-year level",
  legendDisrp20: "≥ 20-year level",
  /** The turbulence key's intensity words (ICAO's light / moderate /
   * severe, with the WAFS severe-forecast threshold splitting moderate);
   * the EDR span after each word is appended untranslated. Keep them as
   * short as an aviation report says them. */
  legendCatLight: "Light",
  legendCatModerate: "Moderate",
  legendCatModerateSevere: "Moderate–severe",
  legendCatSevere: "Severe",
  varLabelTmp2m: "2 m temperature",
  varLabelPrate: "Precipitation rate",
  varLabelDswrf: "Solar radiation",
  varLabelWind10m: "10 m wind",
  varLabelWind80m: "80 m wind",
  varLabelWind100m: "100 m wind",
  varLabelCref: "Composite radar reflectivity",
  varLabelRefl3d: "3D radar reflectivity",
  varLabelCloud3d: "3D cloud water",
  varLabelOrog: "Orography",
  varLabelAurora: "Aurora visibility probability",
  varLabelDis06: "River discharge (6 h mean)",
  varLabelDisrp: "Return-period class",
  varLabelUpstream: "Upstream area",
  varLabelIr104: "Infrared imagery (10.4 µm brightness temperature)",
  varLabelIr039: "Shortwave infrared (3.9 µm brightness temperature)",
  varLabelDustrgb: "Dust RGB (infrared composite)",
  varLabelDustcf: "DEBRA dust confidence",
  varLabelZhouye: "ZHOUYE dust confidence",
  varLabelTruecolor: "True colour by day, infrared cloud by night",
  varLabelGust: "Wind gust",
  varLabelTcdc: "Total cloud cover",
  varLabelCape: "Convective available potential energy",
  varLabelCin: "Convective inhibition",
  varLabelVis: "Visibility",
  varLabelDpt2m: "2 m dew point",
  varLabelAptmp2m: "2 m apparent temperature",
  varLabelLcdc: "Low cloud cover",
  varLabelMcdc: "Middle cloud cover",
  varLabelHcdc: "High cloud cover",
  // Precipitable water is the vapour column's depth; the boundary layer
  // height is a depth too; the precipitation type is the categorical field
  // the four flags are combined into.
  varLabelPwat: "Precipitable water",
  varLabelHpbl: "Planetary boundary layer height",
  varLabelPtype: "Precipitation type",
  // The ocean set. The skin temperature is one field that is the SST over
  // water and the ground's skin over land; the label says both, since the
  // tile reads "SST".
  varLabelTmpsfc: "Sea surface temperature (skin temperature over land)",
  varLabelIcec: "Sea ice cover",
  varLabelIcetk: "Sea ice thickness",
  /** The wave vector: the height as the fill, the direction as particles —
   * the label names both, since the tile reads "WAVE HEIGHT". */
  varLabelWave: "Waves: significant height and direction",
  varLabelHtsgw: "Significant wave height",
  varLabelPerpw: "Primary wave period",
  varLabelDirpw: "Primary wave direction",
  /** The wind sea and the first swell partition, drawn like the waves:
   * the vector labels name height and direction both. */
  varLabelWindwave: "Wind sea: significant height and direction",
  varLabelSwell: "Swell: significant height and direction (first partition)",
  varLabelWvhgt: "Significant height of wind waves",
  varLabelWvper: "Mean period of wind waves",
  varLabelWvdir: "Direction of wind waves",
  varLabelSwhgt: "Significant height of swell (first partition)",
  varLabelSwper: "Mean period of swell (first partition)",
  varLabelSwdir: "Direction of swell (first partition)",
  // The aerosol set: the optical depth at 550 nm for the whole column and
  // by species, and the surface particulate concentrations by size cut.
  varLabelAod: "Aerosol optical depth at 550 nm",
  varLabelAoddust: "Dust optical depth",
  varLabelAodsalt: "Sea salt optical depth",
  varLabelAodsulf: "Sulphate optical depth",
  varLabelAodorg: "Organic carbon optical depth",
  varLabelAodbc: "Black carbon optical depth",
  varLabelPm25: "PM2.5 surface concentration",
  varLabelPm10: "PM10 surface concentration",
  varLabelPm10dust: "Dust PM10 surface concentration",
  // The surface dust concentration of every particle size, as a dust
  // model forecasts it: not the PM10 cut above.
  varLabelDustsfc: "Surface dust concentration",
  // The soil layers, by index from the surface down: the depths are the
  // source's (GFS 0–10/10–40/40–100/100–200 cm, IFS 0–7/7–28/28–100/
  // 100–289 cm), so no label names one.
  varLabelTsoil1: "Soil temperature, layer 1",
  varLabelTsoil2: "Soil temperature, layer 2",
  varLabelTsoil3: "Soil temperature, layer 3",
  varLabelTsoil4: "Soil temperature, layer 4",
  varLabelSoilw1: "Volumetric soil moisture, layer 1",
  varLabelSoilw2: "Volumetric soil moisture, layer 2",
  varLabelSoilw3: "Volumetric soil moisture, layer 3",
  varLabelSoilw4: "Volumetric soil moisture, layer 4",
  varLabelPrmsl: "Mean sea level pressure",
  // One string for eight levels: pressure.ts and levels.ts substitute {level}.
  varLabelHeightAtLevel: "{level} hPa geopotential height",
  varLabelTempAtLevel: "{level} hPa temperature",
  varLabelRhAtLevel: "{level} hPa relative humidity",
  varLabelSpfhAtLevel: "{level} hPa specific humidity",
  varLabelWindAtLevel: "{level} hPa wind",
  varLabelQfluxAtLevel: "{level} hPa water vapour flux",
  varLabelVvelAtLevel: "{level} hPa vertical velocity",
  varLabelThetaeAtLevel: "{level} hPa equivalent potential temperature",
  varLabelCatAtLevel: "{level} hPa clear-air turbulence (EDR)",
  // The 5880 gpm contour, "588" on a Chinese chart — the line the western
  // Pacific subtropical high is defined by.
  legendSubtropicalHigh: "588 line",
  webglUnavailable: "This browser does not provide a WebGL2 context",
  pointerRequestFailed: "Live pointer request returned HTTP {status}",
  manifestRequestFailed: "Manifest request returned HTTP {status}",
  bundleRequestFailed: "Bundle request returned HTTP {status}",
  bundleTooLong: "Bundle exceeds the length declared by the manifest",
  bundleLengthMismatch: "Bundle length does not match the manifest",
  bundleChecksumMismatch: "Bundle checksum mismatch",
  bundleRunMismatch: "Bundle run cycle does not match the manifest",
  bundleMissingVariable: "Bundle is missing variable {id}",
  manifestMissingBundle: "Manifest has no bundle for variable {id}",
  // The geostationary mosaic could open none of its members: every pointer
  // missing or every manifest refused.
  mosaicNoMembers: "No satellite dataset of the mosaic is available",

  // The point probe: click the map to read one grid cell across the axis.
  // Its heading ("POINT") and every number stay English, like the rest of
  // the instrument panel; only the prose below the chart is translated.
  probeAria: "Point data series",
  probeHint: "Play or scrub to fill the series",
  probeComplete: "Series complete",
  probeOutside: "Outside this dataset's grid",
  probeNoData: "No data at this frame",
  probeAwaiting: "Waiting for this frame",
  /** The model's own terrain height, read beside the DEM's (`probeDemLabel`):
   * the two elevations the pin stands together, and the pair a terrain
   * correction is the difference of. Each names its source so neither is
   * mistaken for the other. */
  probeModelElevationLabel: "Model",
  /** The relief the map draws, read beside the model's own terrain. An
   * acronym the field reads as itself in every language, so the dictionary
   * carries the same three letters throughout. */
  probeDemLabel: "DEM",
  probeCloseAria: "Close point data",
  /** The close button on a map card (a storm point, a station, a fire,
   * the warnings in force at a spot). Screen-reader copy only. */
  cardCloseAria: "Close card",

  // The letter on a pressure center, following each country's own chart
  // convention: H/L in English, 高/低 on a Chinese or Japanese chart, 고/저
  // in Korean, H/T in German, A/D in French, A/B in Spanish and Portuguese,
  // В/Н in Russian. Either way it is the one glyph on the map that is not a
  // number.
  centerHigh: "H",
  centerLow: "L",

  // Historical showcase: the list page and the viewer's case banner. The
  // footer link keeps the footer's code-word style in English.
  showcaseLink: "Cases",
  showcaseBack: "← All cases",
  showcaseHome: "Live forecast",
  showcaseTitle: "Showcase cases",
  showcaseHeading: "Weather events, replayed",
  /** The italic sub-title beside the heading. */
  showcaseArchive: "archive",
  showcaseIntro:
    "Each case is a slice of one past weather event — an archived forecast run, or observations — through the same encoder, cropped to the region and hours it happened in.",
  showcaseMetaDescription:
    "Replays of past weather events: cropped forecast and radar datasets for typhoons, rainstorms, heatwaves and more",
  showcaseLoading: "Loading case catalog",
  showcaseEmpty: "No cases published yet",
  showcaseLoadFailed: "Case catalog failed to load: {message}",
  showcaseCaseMissing: "No showcase case named {id}",
  showcaseEventLabel: "Event",
  showcaseRunLabel: "Run",
  showcaseSeriesLabel: "Start",
  showcaseRangeLabel: "Range",
  showcaseSpanLabel: "Span",
  showcaseRegionLabel: "Region",
  showcaseGridLabel: "Grid",
  showcaseSizeLabel: "Size",
  showcaseVariablesLabel: "Fields",
  showcaseHours: "{count} h",
  showcaseCaseAria: "Open case {title}",
  showcaseListAria: "Showcase case list",

  /** The tropical cyclone marks: the rail tile (its gloss and glyph — the
   * CJK locales use their one character for a typhoon, the others C for
   * cyclone, a letter CAPE shares the way solar and skin share S), the
   * storm sheet's heading, groups, rows and chips. Agency
   * and model codes (`NHC`, `JTWC`, `GEFS`) and a storm's class as its
   * centre spells it (`TS`, `TY`) are instrument text and stay as they
   * are. `{count}` is a number. */
  tcTileTip: "Tropical cyclones",
  varTc: "TRACKS",
  glyphTc: "C",
  tcSheetAria: "Tropical cyclones",
  tcHeading: "Tropical cyclones",
  tcAllSystems: "All named systems",
  tcNone: "No active systems",
  tcUnnamed: "Unnamed system",
  tcDisturbances: "Disturbances and depressions",
  tcPotential: "Potential systems",
  tcPotentialShow: "Show {count} model-only systems",
  tcModelOnly: "model only",
  tcAgencies: "Official forecasts",
  tcModels: "Model tracks",
  tcMembers: "Ensemble members",
  tcBest: "Best track",
  tcOptionsAria: "Storm options",

  /** The past tropical cyclones (typhoon.html), linked from the showcase
   * header: the map page's panel picking a storm from the archive by
   * season and basin, the slider over its issue times, the track switches
   * and the table of track errors in km. ATCF ids, basin codes (`WP`), agency and model
   * codes and the truth keys (`USA`, `JMA`) are instrument text and stay as
   * they are. `{name}` is a storm's name with its id, `{id}` an ATCF id. */
  tcHistoryHeading: "Past tropical cyclones",
  tcHistoryIntro: "Every numbered storm in the archive: the centres' best tracks, each forecast made for it, and how far each one missed.",
  tcHistorySeasonsAria: "Season",
  tcHistoryBasinsAria: "Basin",
  tcHistoryAll: "All",
  tcHistoryEmpty: "No storms with forecasts in this season and basin",
  tcHistoryStormAria: "Open {name}",
  tcHistorySouthernNote: "Southern hemisphere seasons run from July to June and are named after the year they end in: the {season} season is {from} to {to}.",
  tcPageTitle: "Past tropical cyclone",
  tcPageMetaDescription: "A past tropical cyclone: the centres' best tracks, every archived forecast made for it, and how far each one missed.",
  tcPageLoading: "Loading storm",
  tcPageMissing: "No archived storm named {id}",
  tcPageFailed: "Storm failed to load: {message}",
  tcPageIssue: "Time",
  tcPageNoForecast: "no forecast starts here",
  tcPagePrev: "Earlier forecast",
  tcPageNext: "Later forecast",
  tcPageSources: "Tracks shown",
  tcPageErrorsHeading: "Track error, km",
  tcPageTruth: "Against the best track of",
  tcPageSource: "Source",
  tcPageErrorsMean: "Mean over the storm",
  tcPageErrorsThis: "The forecast on the map",
  tcPageErrorsNone: "No forecast of this storm has been verified against this best track yet",
  tcPageNote: "Distance to the best track at the synoptic hours, counted only while the system is a tropical or subtropical cyclone; an ensemble is scored by its mean. The official forecasts are set beside models started at the same hour, which finished after the official forecast was written.",
  tcPageId: "ATCF id",
  tcPageLife: "Life",
  tcPagePeak: "Peak wind",
  tcPageForecasts: "Forecasts",
  tcCardSeasons: "Seasons",
  tcCardStorms: "Storms",
  tcPageDetails: "Storm page",
  tcPageSeasonList: "All storms of the season",
  tcCardBrowse: "Browse by season",
  tcPageRadii: "Wind radii",
  tcLegendIntensity: "Intensity: US best track, 1-min wind",

  /** The point products drawn as station marks over whatever is on
   * screen (`docs/sounding.md`, `docs/airport.md`, `docs/synop.md`): the
   * rail tiles —
   * their gloss and glyph, the CJK locales using their own character the
   * way the storm tile does — and the card a clicked mark opens. A
   * station's identifiers (an ICAO id, a WMO number), the flight
   * categories (`VFR`, `LIFR`), `METAR` and every unit (`hPa`, `gpm`,
   * `m/s`) are instrument text and stay as they are; these are the words
   * around them. "Precipitable water" is the depth of the vapour column,
   * "Levels" the number of levels the published ascent carries. The
   * surface stations' tile is national weather-station networks (AMeDAS
   * first); "Station pressure" is the pressure at the station's own
   * height, as opposed to the sea-level reduction, and a network's credit
   * line on the card stays as the network words it. */
  soundingTileTip: "Radiosonde soundings",
  varSounding: "UPPER AIR",
  glyphSounding: "S",
  airportTileTip: "Airport observations",
  varAirport: "METAR",
  glyphAirport: "A",
  synopTileTip: "Surface weather stations",
  varSynop: "SURFACE",
  glyphSynop: "W",
  radarTileTip: "Single-site radar",
  varRadarSite: "SITE",
  glyphRadarSite: "D",
  radarProductAria: "Radar product",
  radarLatest: "newest",
  radarCloseAria: "Back to the mosaic",
  radarReflectivity: "Reflectivity",
  radarVelocity: "Velocity",
  stationSounding: "Sounding",
  stationAirport: "Airport",
  stationObserved: "Observed",
  stationFreezingLevel: "Freezing level",
  stationPw: "Precipitable water",
  stationCategory: "Flight category",
  stationVisibility: "Visibility",
  stationQnh: "QNH",
  stationLevels: "Levels",
  stationSynop: "Weather station",
  stationHumidity: "Humidity",
  stationPrecipitation: "Precipitation",
  stationPressure: "Station pressure",
  stationSeaLevelPressure: "Sea-level pressure",
  stationSunshine: "Sunshine",
  stationSnowDepth: "Snow depth",
  stationElevation: "Elevation",
  /** The card's button: pins the probe on the station, and for a sounding
   * also opens the chart under the panel. */
  stationOpenSounding: "Open sounding",
  stationPinHere: "Pin here",

  /** The active fire marks (`docs/fire.md`): satellite fire detections from
   * NASA FIRMS over the day before the playhead. "Radiative power" is the
   * fire radiative power the product carries (MW); the confidence words
   * are the product's own low / nominal / high classes, and the overpass
   * is the satellite's day or night pass. The sensor names (`VIIRS
   * NOAA-20`) and the credit line stay as the product words them. */
  fireTileTip: "Active fires (NASA FIRMS, last 24 h)",
  varFire: "FIRMS",
  glyphFire: "F",
  fireDetection: "Active fire",
  fireAcquired: "Acquired",
  fireRadiativePower: "Radiative power",
  fireBrightness: "Brightness temperature",
  fireConfidence: "Confidence",
  fireConfidenceLow: "low",
  fireConfidenceNominal: "nominal",
  fireConfidenceHigh: "high",
  fireOverpass: "Overpass",
  fireDay: "day",
  fireNight: "night",
  /** The air-quality stations (`docs/aq.md`): monitoring stations of
   * national networks (US EPA AirNow, Hong Kong EPD), drawn as dots
   * coloured by the station's current PM2.5 concentration. Never call it an
   * index or "AQI": the product publishes concentrations only, and the
   * networks' terms forbid publishing a derived index. `PM2.5`, `PM10`,
   * `O₃` and units are instrument text. "Agency" is the body that runs the
   * station and owns its data; "Network" the national network it reports
   * through. The preliminary note is what the networks' terms require a
   * display of their real-time data to say. `{km}` is a distance. */
  aqTileTip: "Air quality stations (PM2.5)",
  varAq: "PM2.5",
  glyphAq: "Q",
  aqStation: "Air quality",
  aqAgency: "Agency",
  aqNetwork: "Network",
  aqPreliminary: "Preliminary data: not validated, subject to change",
  aqDayRange: "PM2.5, last 24 h",
  aqNearest: "Nearest air-quality station, {km} km",
  /** The weather warnings (`docs/alerts.md`): every warning a national
   * service (US NWS, JMA, ECCC, Europe's through MeteoAlarm) has in force
   * now, drawn as areas on one 1–5 level scale; a snapshot of now, not of
   * the forecast hour on screen. The event names on the card are the services' own and are not
   * translated; the category words are this product's sixteen classes. A
   * JMA warning is in force until it is lifted ("until cancelled"). */
  alertsTileTip: "Weather warnings in force now",
  varAlerts: "WARNINGS",
  glyphAlerts: "!",
  alertsCardTitle: "Warnings",
  alertsInForceNow: "In force now",
  alertsAsOf: "as of {time}",
  alertsLevel: "Level {level}",
  alertsUntilCancelled: "until cancelled",
  alertsIssued: "issued {time}",
  alertCategoryRain: "Heavy rain",
  alertCategoryFlood: "Flood",
  alertCategoryLandslide: "Landslide",
  alertCategoryWind: "Wind",
  alertCategoryThunderstorm: "Thunderstorm",
  alertCategorySnowIce: "Snow and ice",
  alertCategoryHeat: "Heat",
  alertCategoryCold: "Cold",
  alertCategoryFog: "Fog",
  alertCategoryDust: "Dust",
  alertCategoryFire: "Fire weather",
  alertCategoryAir: "Air quality",
  alertCategoryCoastal: "Coastal",
  alertCategoryMarine: "Marine",
  alertCategoryTsunami: "Tsunami",
  alertCategoryOther: "Other",
  /** A showcase case's markers (`casemarkers.ts`): labelled points of
   * context on the map. The kind word heads the card a marker opens;
   * `markerSources` heads its list of links; `caseMarkersAria` names
   * the list of markers under the case summary. */
  markerPlace: "Place",
  markerEvent: "Event",
  markerSources: "Sources",
  caseMarkersAria: "Places and events in this case",
  /** The row of links to the other parts of an event split across cases. */
  casePartsAria: "Parts of this event",
  /** A showcase case's river data (`river/`, docs/river.md): the block
   * in the case panel — `riverHeading`, the track switch (`riverTracksAria`;
   * the analysis, or a forecast named by its UTC cycle, which stays an
   * instrument stamp), the key of flow against the reach's usual flow
   * (`riverRatio*`, numerals in every locale) or, on a case coloured
   * through a bundle's palette, the note that says so, the flood extents' switch,
   * the hint that the circles are gauges — and the gauge's hydrograph in
   * the probe panel: the two series' words and the peaks list's heading.
   * The index's disclaimer and credits are shown as published, in English. */
  riverHeading: "River flow",
  riverTracksAria: "River model run",
  riverTrackAnalysis: "Analysis",
  riverTrackForecast: "Forecast {cycle}",
  riverKeyAria: "Flow against its usual flow",
  riverRatio2: "< 2× usual",
  riverRatio10: "2–10×",
  riverRatio100: "10–100×",
  riverRatioMax: "≥ 100×",
  riverKeyField: "Coloured by flow, on the field’s scale",
  riverExtents: "Flood extents",
  riverGaugeHint: "Circles are gauges: click one for its hydrograph.",
  riverObserved: "observed",
  riverModelled: "model",
  riverPeaks: "Reported peaks",
  riverGaugeLoading: "loading…",
  riverGaugeUnavailable: "unavailable",

  /** The sounding section of the pinned point's panel: the nearest ascent
   * drawn as a skew-T under the meteogram rows, and the full-height sheet
   * the chart opens into.
   *
   * `soundingObserved` and `soundingModel` are the two words the chart's
   * legend and every readout tell the curves apart by — the solid trace the
   * sonde measured and the dotted one the run forecast — and the same
   * `soundingObserved` labels the airport's measurement beside a row's
   * readout. Keep both to four or five characters: they sit inside a legend
   * line that also carries the run's name, and beside a number in a row
   * 30 px tall. Everything around them is instrument text and is not
   * translated — the station's WMO number, the distance in kilometres, the
   * pressure in hPa, the flight category (`VFR`), `PROB40`.
   *
   * `soundingNoModel` is why there is only one curve: the run has no frame
   * within twelve hours of the ascent. Within that, the legend names both
   * times and the gap (`+6 h`), so the comparison is always labelled. */
  soundingObserved: "OBS",
  soundingModel: "MODEL",
  soundingNoModel: "no model within 12 h",
  soundingLoading: "Reading the ascent…",
  soundingUnavailable: "Ascent unavailable",
  soundingExpand: "Full chart",
  soundingClose: "Close",
  soundingSheetHeading: "Sounding",
  soundingSheetAria: "Sounding chart",

  /* The comparison page (compare.html): every live forecast model at one
   * point on one clock, Windy's "compare" shape — one block per model,
   * hourly columns, a row per field — with the nearest airport's reports
   * as a block of their own. The heading is display type and short.
   * `compareLink` is the probe panel's button into the page and keep it to one
   * short word.
   *
   * `compareSpan5` / `compareSpan15` are the two horizons, a pair of
   * chips, and `compareLatitude` / `compareLongitude` label two numeric
   * inputs. `compareHide` / `compareShow` / `comparePin` are per-model
   * buttons; `compareHidden` heads the list of blocks taken off the page.
   * `compareLoading` names the model still being read, and
   * `compareModelFailed` carries the decoder's own English message after
   * the model's name. `compareObservationsFrom` heads the airport block
   * with the ICAO id (`RJTT`), which stays as it is. `compareCells` is the
   * one-line note under the blocks on what a value is. */
  compareLink: "Compare",
  compareTitle: "Compare forecasts",
  compareHeading: "Model comparison",
  compareEyebrow: "point",
  compareIntro:
    "Every live forecast model at one point, on one clock: what each run says for the hours ahead, beside the nearest airport's own reports.",
  compareMetaDescription:
    "Compare GFS, ECMWF, AIFS, IFS HRES and HRRR forecasts at one point, hour by hour, against the nearest airport's METAR reports.",
  compareLatitude: "Latitude",
  compareLongitude: "Longitude",
  compareLocate: "Use my location",
  compareLocateFailed: "Location unavailable",
  compareOnMap: "On the map",
  compareSpan5: "5 days",
  compareSpan15: "15 days",
  compareFieldsAria: "Fields to compare",
  compareModelsAria: "Model comparison blocks",
  compareLoading: "Reading {model}…",
  compareModelFailed: "{model}: {message}",
  compareOffGrid: "outside this model's grid",
  compareNoModels: "No model covers this point",
  compareHide: "Hide",
  compareShow: "Show",
  comparePin: "Pin to top",
  compareHidden: "Hidden models",
  compareRun: "Run",
  compareObservationsFrom: "Reports from {icao}",
  compareNoAirport: "No airport within 40 km",
  compareHours: "Hours",
  compareNow: "now",
  compareCells: "Each value is the model's own grid cell nearest the point, never an interpolation; times read in your zone.",
  /* The run-metrics page (metrics.html): when each forecast run went live,
   * measured from the publish workflows (docs/metrics.md). The heading is
   * display type, as on the comparison page. `metricsNotSla` must say plainly that these are
   * measurements and no availability commitment. Fact labels
   * (`metricsLatest` … `metricsRuns`) sit in sentence case over a narrow
   * value: keep them to two or three words. Values beside them — run codes
   * (`2026-10-12 06Z`), h:mm latencies, `P90` — are instrument text and
   * stay as they are. `metricsTypical` heads an experience value, not a
   * measurement, and `metricsTypicalNote` (starting with the same `*`)
   * says so. `metricsOutcome*` follow a count (`11 landed`).
   * `metricsCommonest` heads the column of the cycle most often live at a
   * deadline, and `metricsNoneLive` fills a day with nothing live yet.
   * `metricsFailed` carries an English diagnostic after the colon. */
  metricsMetaDescription: "When each Xue forecast run went live, measured from the publish workflows: latency after the cycle time, missing cycles, and what was live at the ERCOT day-ahead close.",
  metricsHeading: "Run arrival",
  metricsEyebrow: "measured",
  metricsIntro: "When each forecast run went live on this site, read from the publish workflows' own records: the moment the live pointer was written, and how long after the model's cycle time that was.",
  metricsNotSla: "These are measurements, not a service level: Xue makes no availability commitment.",
  metricsGenerated: "Built {time}, last {days} days",
  metricsLoading: "Reading metrics…",
  metricsEmpty: "No metrics yet. They are built every six hours from the publish workflows; check back after the next build.",
  metricsFailed: "Metrics could not be read: {message}",
  metricsSourcesHeading: "By source",
  metricsLatencyNote: "Latency runs from the cycle time to the moment the run went live, so it includes the model's own production time. All times are UTC.",
  metricsTimelineNote: "Each strip is the window: one bar per cycle that went live, at its cycle time, its height the latency against the source's own longest; a mark under the line is a cycle that never arrived.",
  metricsTypicalNote: "* Typical upstream: an experience value for when the model's own data is usually complete after its cycle time, not a measurement. Blank where there is none.",
  metricsTimelineAria: "{model} arrivals over the window",
  metricsCadence: "every {hours} h",
  metricsLatest: "Latest run",
  metricsLanded: "Live at",
  metricsLatency: "Latency",
  metricsMedian: "Median",
  metricsMax: "Max",
  metricsTypical: "Typical upstream",
  metricsCycles: "Cycles landed",
  metricsMissing: "Missing",
  metricsMissingList: "Missing cycles",
  metricsRuns: "Workflow runs",
  metricsOutcomeLanded: "landed",
  metricsOutcomeSkipped: "skipped",
  metricsOutcomeDryRun: "dry run",
  metricsOutcomeFailed: "failed",
  metricsBacklog: "{count} workflow runs not read yet; the next build reads them.",
  metricsDeadlinesHeading: "At the deadline",
  metricsDeadlineIntro: "For each local day, the newest run already live at the cutoff, and how old its cycle was then.",
  metricsSource: "Source",
  metricsCommonest: "Usually",
  metricsNoneLive: "none",
} as const;

/** Every key the UI can ask for. Each locale module is typed against this,
 * so a missing or stray key is a compile error rather than a blank label. */
export type MessageKey = keyof typeof en;
